/**
 * RecurringJobManager — Recurring series indicator + View Plan / Edit Future /
 * Cancel Future / Change Pattern dialogs for a job that belongs to a recurrence group.
 */
import { useState, useCallback, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DatePicker } from './ui/date-picker';
import { TimeField } from './ui/time-field';
import { Repeat, Calendar, Eye, Pencil, XCircle, RefreshCw, Loader2, Check, X, Plus, Minus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const WEEKDAYS = [
  { value: 0, label: 'Mon' }, { value: 1, label: 'Tue' }, { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' }, { value: 4, label: 'Fri' }, { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];
const ORDINALS = [
  { value: 1, label: '1st' }, { value: 2, label: '2nd' }, { value: 3, label: '3rd' },
  { value: 4, label: '4th' }, { value: -1, label: 'Last' },
];
const MONTHS_LIST = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' },
];
const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const INTERVALS = Array.from({ length: 99 }, (_, i) => i + 1);
const ORD_LABELS = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', '-1': 'Last' };
const MONTH_ABBR = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TERMINAL_STATUSES = new Set(['docketed', 'invoiced', 'cancelled']);

function formatDateShort(iso) {
  try { return format(parseISO(iso), 'dd MMM yyyy'); } catch { return iso; }
}
function dayName(iso) {
  try { return format(parseISO(iso), 'EEE'); } catch { return ''; }
}

function StatusBadge({ status }) {
  const colors = {
    not_allocated: 'bg-gray-500/20 text-gray-400',
    allocated: 'bg-blue-500/20 text-blue-400',
    confirmed: 'bg-green-500/20 text-green-400',
    tbc: 'bg-amber-500/20 text-amber-400',
    docketed: 'bg-purple-500/20 text-purple-400',
    invoiced: 'bg-cyan-500/20 text-cyan-400',
    cancelled: 'bg-red-500/20 text-red-400',
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium capitalize ${colors[status] || 'bg-muted text-muted-foreground'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
}

function patternLabel(config) {
  if (!config) return '';
  const p = config.pattern;
  const intv = config.interval || 1;
  if (p === 'daily') return intv > 1 ? `Every ${intv} days` : 'Daily';
  if (p === 'weekly' || p === 'fortnightly') {
    const days = (config.weekdays || []).map(d => WEEKDAYS.find(w => w.value === d)?.label).filter(Boolean).join(', ');
    const wi = p === 'fortnightly' ? 2 : intv;
    const prefix = wi === 1 ? 'Weekly' : wi === 2 ? 'Fortnightly' : `Every ${wi} weeks`;
    return `${prefix}${days ? ` on ${days}` : ''}`;
  }
  if (p === 'monthly') {
    const days = (config.month_days || []).join(', ');
    const prefix = intv > 1 ? `Every ${intv} months` : 'Monthly';
    return `${prefix} on day${config.month_days?.length > 1 ? 's' : ''} ${days}`;
  }
  if (p === 'monthly_relative') {
    const ords = (config.ordinal_weeks || []).map(o => ORD_LABELS[String(o)] || o).join(' & ');
    const days = (config.rel_weekdays || []).map(d => WEEKDAYS.find(w => w.value === d)?.label).filter(Boolean).join(', ');
    const prefix = intv > 1 ? `Every ${intv} months` : 'Monthly';
    return `${prefix} on ${ords} ${days}`;
  }
  if (p === 'yearly') {
    const mo = MONTH_ABBR[config.year_month] || '';
    const days = (config.month_days || []).join(', ');
    return `Yearly on ${mo} ${days}`;
  }
  if (p === 'yearly_relative') {
    const ords = (config.ordinal_weeks || []).map(o => ORD_LABELS[String(o)] || o).join(' & ');
    const days = (config.rel_weekdays || []).map(d => WEEKDAYS.find(w => w.value === d)?.label).filter(Boolean).join(', ');
    const mo = MONTH_ABBR[config.year_month] || '';
    return `Yearly on ${ords} ${days} of ${mo}`;
  }
  if (p === 'custom') return 'Custom dates';
  return p;
}

/** Fallback for legacy jobs: build a minimal config from recurrence_pattern string */
function fallbackConfig(job) {
  if (job.recurrence_config) return job.recurrence_config;
  if (!job.recurrence_pattern) return null;
  return { pattern: job.recurrence_pattern };
}

// ─── Indicator Bar ────────────────────────────────────────────────
export function RecurringIndicator({ job, onViewPlan, onEditFuture, onChangePattern }) {
  if (!job?.recurrence_group_id) return null;
  const idx = (job.recurrence_index ?? 0) + 1;
  const total = job.recurrence_total || '?';
  const config = fallbackConfig(job);
  const label = patternLabel(config);
  const isTerminal = TERMINAL_STATUSES.has(job.status);

  return (
    <div className="flex items-center gap-2 p-2.5 bg-amber-500/8 border border-amber-500/25 rounded-md" data-testid="recurring-indicator">
      <Repeat className="h-4 w-4 text-amber-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium">Recurring</span>
        <span className="text-xs text-muted-foreground ml-1.5">
          Job {idx} of {total}{label ? ` \u00b7 ${label}` : ''}
        </span>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={onViewPlan} data-testid="recurring-view-plan-btn">
          <Eye className="h-3 w-3 mr-1" /> Plan
        </Button>
        {!isTerminal && (
          <>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={onEditFuture} data-testid="recurring-edit-future-btn">
              <Pencil className="h-3 w-3 mr-1" /> Edit Future
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={onChangePattern} data-testid="recurring-change-pattern-btn">
              <RefreshCw className="h-3 w-3 mr-1" /> Pattern
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── View Plan Dialog ─────────────────────────────────────────────
export function ViewPlanDialog({ open, onOpenChange, groupId, currentJobId, api, onNavigateToJob, onCancelFuture }) {
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [recurrenceConfig, setRecurrenceConfig] = useState(null);

  const fetchPlan = useCallback(async () => {
    if (!groupId || !api) return;
    setLoading(true);
    try {
      const res = await api.get(`/jobs/recurring/${groupId}`);
      setJobs(res.data.jobs || []);
      setRecurrenceConfig(res.data.recurrence_config || null);
    } catch (err) {
      toast.error('Failed to load recurrence plan');
    } finally {
      setLoading(false);
    }
  }, [groupId, api]);

  useEffect(() => { if (open) fetchPlan(); }, [open, fetchPlan]);

  const today = new Date().toISOString().slice(0, 10);
  const activeJobs = jobs.filter(j => j.status !== 'cancelled');
  const cancelledJobs = jobs.filter(j => j.status === 'cancelled');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="view-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-amber-400" /> Recurrence Plan
          </DialogTitle>
          <DialogDescription>
            {recurrenceConfig ? patternLabel(recurrenceConfig) : ''} — {activeJobs.length} active occurrence{activeJobs.length !== 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-0.5 p-1">
              {activeJobs.map((j) => {
                const isPast = j.start_date < today;
                const isCurrent = j.id === currentJobId;
                return (
                  <button
                    key={j.id}
                    type="button"
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded text-sm text-left transition-colors
                      ${isCurrent ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/50'}
                      ${isPast && !isCurrent ? 'opacity-50' : ''}`}
                    onClick={() => { onOpenChange(false); onNavigateToJob?.(j); }}
                    data-testid={`plan-job-${j.id}`}
                  >
                    <span className="text-muted-foreground w-5 text-right text-[10px]">{(j.recurrence_index ?? 0) + 1}.</span>
                    <span className="flex-1 font-medium">{formatDateShort(j.start_date)}</span>
                    <span className="text-xs text-muted-foreground w-8">{dayName(j.start_date)}</span>
                    <span className="text-xs text-muted-foreground w-16">#{j.job_number}</span>
                    <StatusBadge status={j.status} />
                    {isCurrent && <Badge variant="secondary" className="text-[9px] px-1">Current</Badge>}
                  </button>
                );
              })}
              {cancelledJobs.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-muted-foreground cursor-pointer px-3 py-1">
                    {cancelledJobs.length} cancelled
                  </summary>
                  {cancelledJobs.map(j => (
                    <div key={j.id} className="flex items-center gap-3 px-3 py-1.5 text-sm opacity-40">
                      <span className="text-muted-foreground w-5 text-right text-[10px]">-</span>
                      <span className="flex-1 line-through">{formatDateShort(j.start_date)}</span>
                      <span className="text-xs text-muted-foreground w-16">#{j.job_number}</span>
                      <StatusBadge status="cancelled" />
                    </div>
                  ))}
                </details>
              )}
            </div>
          </ScrollArea>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="destructive" size="sm" onClick={onCancelFuture} data-testid="plan-cancel-future-btn">
            <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel Future
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit Future Dialog ───────────────────────────────────────────
export function EditFutureDialog({ open, onOpenChange, groupId, job, api, onSuccess }) {
  const [fromDate, setFromDate] = useState('');
  const [updates, setUpdates] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && job) {
      setFromDate(job.start_date || '');
      setUpdates({
        description: job.description || '',
        notes: job.notes || '',
        po_number: job.po_number || '',
        start_time: job.start_time || '',
        end_time: job.end_time || '',
      });
    }
  }, [open, job]);

  const handleSubmit = async () => {
    if (!fromDate) { toast.error('Select a start date'); return; }
    const filtered = {};
    for (const [k, v] of Object.entries(updates)) {
      if (v !== '' && v !== undefined) filtered[k] = v;
    }
    if (!Object.keys(filtered).length) { toast.error('No changes to apply'); return; }
    setLoading(true);
    try {
      const res = await api.put(`/jobs/recurring/${groupId}/future`, {
        updates: filtered,
        from_date: fromDate,
      });
      toast.success(`Updated ${res.data.modified_count} job(s)`);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-future-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-blue-400" /> Edit Future Occurrences
          </DialogTitle>
          <DialogDescription>Changes apply to all non-terminal jobs on or after the selected date.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase text-muted-foreground">Apply from</Label>
            <DatePicker value={fromDate} onChange={setFromDate} testId="edit-future-from-date" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase text-muted-foreground">Description</Label>
            <Input value={updates.description || ''} onChange={e => setUpdates(u => ({ ...u, description: e.target.value }))} data-testid="edit-future-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase text-muted-foreground">Start Time</Label>
              <TimeField value={updates.start_time || ''} onChange={v => setUpdates(u => ({ ...u, start_time: v }))} data-testid="edit-future-start-time" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase text-muted-foreground">End Time</Label>
              <TimeField value={updates.end_time || ''} onChange={v => setUpdates(u => ({ ...u, end_time: v }))} data-testid="edit-future-end-time" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase text-muted-foreground">PO Number</Label>
            <Input value={updates.po_number || ''} onChange={e => setUpdates(u => ({ ...u, po_number: e.target.value }))} data-testid="edit-future-po" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase text-muted-foreground">Notes</Label>
            <Textarea rows={2} value={updates.notes || ''} onChange={e => setUpdates(u => ({ ...u, notes: e.target.value }))} data-testid="edit-future-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" size="sm" onClick={handleSubmit} disabled={loading} data-testid="edit-future-apply-btn">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
            Apply to Future
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Cancel Future Dialog ─────────────────────────────────────────
export function CancelFutureDialog({ open, onOpenChange, groupId, fromDate, api, onSuccess }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setReason(''); }, [open]);

  const handleSubmit = async () => {
    if (!reason.trim()) { toast.error('Please provide a reason'); return; }
    setLoading(true);
    try {
      const res = await api.post(`/jobs/recurring/${groupId}/cancel-future`, {
        reason: reason.trim(),
        from_date: fromDate,
      });
      toast.success(`Cancelled ${res.data.cancelled_count} job(s)`);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="cancel-future-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <XCircle className="h-5 w-5" /> Cancel Future Occurrences
          </DialogTitle>
          <DialogDescription>
            This will cancel all non-terminal jobs on or after {formatDateShort(fromDate)}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label className="text-xs uppercase text-muted-foreground">Cancellation Reason</Label>
          <Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Project on hold" data-testid="cancel-future-reason" />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>Back</Button>
          <Button type="button" variant="destructive" size="sm" onClick={handleSubmit} disabled={loading} data-testid="cancel-future-confirm-btn">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
            Confirm Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Change Pattern Dialog ────────────────────────────────────────
export function ChangePatternDialog({ open, onOpenChange, groupId, currentConfig, fromDate: initialFromDate, api, onSuccess }) {
  const [fromDate, setFromDate] = useState('');
  const [pattern, setPattern] = useState('weekly');
  const [interval, setIntervalVal] = useState(1);
  const [weekdays, setWeekdays] = useState([]);
  const [monthDays, setMonthDays] = useState([]);
  const [monthlyMode, setMonthlyMode] = useState('absolute');
  const [ordinalWeeks, setOrdinalWeeks] = useState([]);
  const [relWeekdays, setRelWeekdays] = useState([]);
  const [yearMonth, setYearMonth] = useState(1);
  const [yearlyMode, setYearlyMode] = useState('absolute');
  const [endType, setEndType] = useState('count');
  const [endDate, setEndDate] = useState('');
  const [repeatCount, setRepeatCount] = useState(4);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDates, setPreviewDates] = useState([]);
  const [existingDates, setExistingDates] = useState(new Set());
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    if (!open) return;
    setFromDate(initialFromDate || '');

    const applyConfig = (cfg) => {
      if (!cfg) return;
      let p = cfg.pattern || 'weekly';
      let intv = cfg.interval || 1;
      // backward compat
      if (p === 'fortnightly') { p = 'weekly'; intv = 2; }
      if (p === 'monthly_relative') { p = 'monthly'; setMonthlyMode('relative'); } else if (p === 'monthly') { setMonthlyMode('absolute'); }
      if (p === 'yearly_relative') { p = 'yearly'; setYearlyMode('relative'); } else if (p === 'yearly') { setYearlyMode('absolute'); }
      setPattern(p);
      setIntervalVal(intv);
      setWeekdays(cfg.weekdays || []);
      setMonthDays(cfg.month_days || []);
      setOrdinalWeeks(cfg.ordinal_weeks || []);
      setRelWeekdays(cfg.rel_weekdays || []);
      setYearMonth(cfg.year_month || 1);
      setEndType(cfg.end_type || 'count');
      setEndDate(cfg.end_date || '');
      setRepeatCount(cfg.repeat_count || 4);
    };

    if (currentConfig) { applyConfig(currentConfig); }
    else if (groupId && api) {
      (async () => {
        try { const res = await api.get(`/jobs/recurring/${groupId}`); applyConfig(res.data.recurrence_config); } catch { /* ignore */ }
      })();
    }
  }, [open, currentConfig, initialFromDate, groupId, api]);

  useEffect(() => {
    if (!open || !groupId || !api) return;
    (async () => {
      try {
        const res = await api.get(`/jobs/recurring/${groupId}`);
        setExistingDates(new Set((res.data.jobs || []).filter(j => j.status !== 'cancelled').map(j => j.start_date)));
      } catch { /* ignore */ }
    })();
  }, [open, groupId, api]);

  const buildPatternConfig = () => {
    let ep = pattern;
    if (pattern === 'monthly' && monthlyMode === 'relative') ep = 'monthly_relative';
    if (pattern === 'yearly' && yearlyMode === 'relative') ep = 'yearly_relative';
    return {
      pattern: ep, interval,
      weekdays: ep === 'weekly' ? weekdays : [],
      month_days: (ep === 'monthly' || ep === 'yearly') ? monthDays : [],
      ordinal_weeks: (ep === 'monthly_relative' || ep === 'yearly_relative') ? ordinalWeeks : [],
      rel_weekdays: (ep === 'monthly_relative' || ep === 'yearly_relative') ? relWeekdays : [],
      year_month: (ep === 'yearly' || ep === 'yearly_relative') ? yearMonth : undefined,
      custom_dates: [],
      end_type: endType,
      end_date: endType === 'date' ? endDate : undefined,
      repeat_count: endType === 'count' ? repeatCount : undefined,
    };
  };

  useEffect(() => {
    if (!open || !fromDate || !api) return;
    const config = buildPatternConfig();
    const ep = config.pattern;
    if (ep === 'weekly' && !weekdays.length) { setPreviewDates([]); return; }
    if (ep === 'monthly' && !monthDays.length) { setPreviewDates([]); return; }
    if (ep === 'monthly_relative' && (!ordinalWeeks.length || !relWeekdays.length)) { setPreviewDates([]); return; }
    if (ep === 'yearly' && !monthDays.length) { setPreviewDates([]); return; }
    if (ep === 'yearly_relative' && (!ordinalWeeks.length || !relWeekdays.length)) { setPreviewDates([]); return; }
    if (endType === 'date' && !endDate) { setPreviewDates([]); return; }
    if (endType === 'count' && (!repeatCount || repeatCount < 1)) { setPreviewDates([]); return; }

    const timer = setTimeout(async () => {
      setPreviewLoading(true); setPreviewError('');
      try {
        const res = await api.post('/jobs/recurring/preview', { job: { start_date: fromDate }, recurrence: config });
        setPreviewDates(res.data?.dates || []);
      } catch (err) { setPreviewError(err.response?.data?.detail || 'Preview failed'); setPreviewDates([]); }
      finally { setPreviewLoading(false); }
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fromDate, pattern, interval, weekdays, monthDays, monthlyMode, ordinalWeeks, relWeekdays, yearMonth, yearlyMode, endType, endDate, repeatCount, api]);

  const toggleWeekday = (d) => setWeekdays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());
  const toggleMonthDay = (d) => setMonthDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort((a, b) => a - b));
  const toggleOrdinal = (v) => setOrdinalWeeks(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v].sort((a, b) => (a < 0 ? 5 : a) - (b < 0 ? 5 : b)));
  const toggleRelWeekday = (d) => setRelWeekdays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());

  const futureDates = new Set([...existingDates].filter(d => d >= fromDate));
  const newDatesSet = new Set(previewDates);
  const kept = previewDates.filter(d => futureDates.has(d));
  const added = previewDates.filter(d => !futureDates.has(d));
  const removed = [...futureDates].filter(d => !newDatesSet.has(d));

  const intervalUnit = { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' }[pattern] || '';

  const handleApply = async () => {
    if (!fromDate) { toast.error('Select a start date'); return; }
    setLoading(true);
    try {
      const res = await api.put(`/jobs/recurring/${groupId}/pattern`, { recurrence: buildPatternConfig(), from_date: fromDate });
      toast.success(`Pattern changed: ${res.data.kept} kept, ${res.data.cancelled} cancelled, ${res.data.created} created`);
      onOpenChange(false); onSuccess?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change pattern'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="change-pattern-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-amber-400" /> Change Recurrence Pattern
          </DialogTitle>
          <DialogDescription>Modify the pattern for future occurrences. Past and terminal jobs are untouched.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          {/* Left: Pattern config */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase text-muted-foreground">Apply from date</Label>
              <DatePicker value={fromDate} onChange={setFromDate} testId="pattern-from-date" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase text-muted-foreground">Pattern</Label>
              <Select value={pattern} onValueChange={(v) => { setPattern(v); if (v === 'monthly') setMonthlyMode('absolute'); if (v === 'yearly') setYearlyMode('absolute'); }}>
                <SelectTrigger data-testid="pattern-type-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Interval */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase text-muted-foreground">Frequency</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Every</span>
                <Select value={String(interval)} onValueChange={v => setIntervalVal(parseInt(v))}>
                  <SelectTrigger className="w-[72px] h-8" data-testid="pattern-interval-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {INTERVALS.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">{intervalUnit}{interval !== 1 ? 's' : ''}</span>
              </div>
            </div>

            {/* Weekly: Weekday Picker */}
            {pattern === 'weekly' && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase text-muted-foreground">Repeat on</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="pattern-weekday-picker">
                  {WEEKDAYS.map(({ value, label }) => (
                    <Button key={value} type="button" size="sm"
                      variant={weekdays.includes(value) ? 'default' : 'outline'}
                      className={`h-8 px-3 text-xs ${weekdays.includes(value) ? 'bg-primary text-primary-foreground' : ''}`}
                      onClick={() => toggleWeekday(value)}>{label}</Button>
                  ))}
                </div>
              </div>
            )}

            {/* Monthly */}
            {pattern === 'monthly' && (
              <div className="space-y-3">
                <div className="flex gap-1 p-0.5 bg-muted rounded-md w-fit" data-testid="pattern-monthly-mode">
                  <button type="button"
                    className={`px-3 py-1.5 text-xs rounded font-medium transition-colors ${monthlyMode === 'absolute' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setMonthlyMode('absolute')}>Day of month</button>
                  <button type="button"
                    className={`px-3 py-1.5 text-xs rounded font-medium transition-colors ${monthlyMode === 'relative' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setMonthlyMode('relative')}>Specific weekday</button>
                </div>
                {monthlyMode === 'absolute' ? (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase text-muted-foreground">Day(s) of month</Label>
                    <div className="flex flex-wrap gap-1" data-testid="pattern-monthday-picker">
                      {MONTH_DAYS.map(day => (
                        <Button key={day} type="button" size="sm"
                          variant={monthDays.includes(day) ? 'default' : 'outline'}
                          className={`h-7 w-8 p-0 text-xs ${monthDays.includes(day) ? 'bg-primary text-primary-foreground' : ''}`}
                          onClick={() => toggleMonthDay(day)}>{day}</Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase text-muted-foreground">Position</Label>
                      <div className="flex flex-wrap gap-1.5" data-testid="pattern-ordinal-picker">
                        {ORDINALS.map(({ value, label }) => (
                          <Button key={value} type="button" size="sm"
                            variant={ordinalWeeks.includes(value) ? 'default' : 'outline'}
                            className={`h-8 px-3 text-xs ${ordinalWeeks.includes(value) ? 'bg-primary text-primary-foreground' : ''}`}
                            onClick={() => toggleOrdinal(value)}>{label}</Button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase text-muted-foreground">On</Label>
                      <div className="flex flex-wrap gap-1.5" data-testid="pattern-rel-weekday-picker">
                        {WEEKDAYS.map(({ value, label }) => (
                          <Button key={value} type="button" size="sm"
                            variant={relWeekdays.includes(value) ? 'default' : 'outline'}
                            className={`h-8 px-3 text-xs ${relWeekdays.includes(value) ? 'bg-primary text-primary-foreground' : ''}`}
                            onClick={() => toggleRelWeekday(value)}>{label}</Button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Yearly */}
            {pattern === 'yearly' && (
              <div className="space-y-3">
                <div className="flex gap-1 p-0.5 bg-muted rounded-md w-fit" data-testid="pattern-yearly-mode">
                  <button type="button"
                    className={`px-3 py-1.5 text-xs rounded font-medium transition-colors ${yearlyMode === 'absolute' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setYearlyMode('absolute')}>On date</button>
                  <button type="button"
                    className={`px-3 py-1.5 text-xs rounded font-medium transition-colors ${yearlyMode === 'relative' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setYearlyMode('relative')}>On weekday</button>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase text-muted-foreground">Month</Label>
                  <Select value={String(yearMonth)} onValueChange={v => setYearMonth(parseInt(v))}>
                    <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS_LIST.map(({ value, label }) => <SelectItem key={value} value={String(value)}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {yearlyMode === 'absolute' ? (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase text-muted-foreground">Day(s) of month</Label>
                    <div className="flex flex-wrap gap-1" data-testid="pattern-yearly-monthday-picker">
                      {MONTH_DAYS.map(day => (
                        <Button key={day} type="button" size="sm"
                          variant={monthDays.includes(day) ? 'default' : 'outline'}
                          className={`h-7 w-8 p-0 text-xs ${monthDays.includes(day) ? 'bg-primary text-primary-foreground' : ''}`}
                          onClick={() => toggleMonthDay(day)}>{day}</Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase text-muted-foreground">Position</Label>
                      <div className="flex flex-wrap gap-1.5" data-testid="pattern-yearly-ordinal-picker">
                        {ORDINALS.map(({ value, label }) => (
                          <Button key={value} type="button" size="sm"
                            variant={ordinalWeeks.includes(value) ? 'default' : 'outline'}
                            className={`h-8 px-3 text-xs ${ordinalWeeks.includes(value) ? 'bg-primary text-primary-foreground' : ''}`}
                            onClick={() => toggleOrdinal(value)}>{label}</Button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase text-muted-foreground">On</Label>
                      <div className="flex flex-wrap gap-1.5" data-testid="pattern-yearly-rel-weekday-picker">
                        {WEEKDAYS.map(({ value, label }) => (
                          <Button key={value} type="button" size="sm"
                            variant={relWeekdays.includes(value) ? 'default' : 'outline'}
                            className={`h-8 px-3 text-xs ${relWeekdays.includes(value) ? 'bg-primary text-primary-foreground' : ''}`}
                            onClick={() => toggleRelWeekday(value)}>{label}</Button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs uppercase text-muted-foreground">Ends</Label>
              <div className="flex items-center gap-3">
                <Select value={endType} onValueChange={setEndType}>
                  <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date">On date</SelectItem>
                    <SelectItem value="count">After</SelectItem>
                  </SelectContent>
                </Select>
                {endType === 'date' ? (
                  <DatePicker value={endDate} onChange={setEndDate} minDate={fromDate} testId="pattern-end-date" className="flex-1" />
                ) : (
                  <div className="flex items-center gap-2">
                    <Input type="number" min={1} max={365} value={repeatCount}
                      onChange={e => setRepeatCount(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
                      className="w-20 h-8 text-sm" data-testid="pattern-repeat-count" />
                    <span className="text-sm text-muted-foreground">occurrences</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Preview diff */}
          <div className="space-y-3 border-l pl-4">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase text-muted-foreground flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> Preview
                {previewDates.length > 0 && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-1">{previewDates.length}</Badge>
                )}
              </Label>
              {previewLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            </div>
            {previewError && (
              <div className="flex items-center gap-1.5 text-xs text-destructive"><AlertCircle className="h-3.5 w-3.5" /> {previewError}</div>
            )}
            {previewDates.length > 0 && (
              <div className="flex gap-3 text-xs">
                <span className="text-green-400">{kept.length} kept</span>
                <span className="text-blue-400">{added.length} new</span>
                <span className="text-red-400">{removed.length} cancelled</span>
              </div>
            )}
            <ScrollArea className="max-h-[280px] rounded border border-border">
              <div className="p-2 space-y-0.5">
                {previewDates.map((d, i) => {
                  const isKept = futureDates.has(d);
                  return (
                    <div key={d} className={`flex items-center gap-2 text-sm py-0.5 px-1.5 rounded ${isKept ? '' : 'bg-blue-500/10'}`}
                      data-testid={`preview-date-${d}`}>
                      <span className="text-[10px] text-muted-foreground w-5 text-right">{i + 1}.</span>
                      <span className="flex-1">{formatDateShort(d)}</span>
                      <span className="text-xs text-muted-foreground w-8">{dayName(d)}</span>
                      {isKept ? <Check className="h-3 w-3 text-green-400" /> : <Badge variant="secondary" className="text-[9px] px-1 text-blue-400 bg-blue-500/15">New</Badge>}
                    </div>
                  );
                })}
                {removed.sort().map(d => (
                  <div key={`rm-${d}`} className="flex items-center gap-2 text-sm py-0.5 px-1.5 rounded bg-red-500/10 opacity-60">
                    <span className="text-[10px] text-muted-foreground w-5 text-right">-</span>
                    <span className="flex-1 line-through">{formatDateShort(d)}</span>
                    <span className="text-xs text-muted-foreground w-8">{dayName(d)}</span>
                    <X className="h-3 w-3 text-red-400" />
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" size="sm" onClick={handleApply} disabled={loading || previewDates.length === 0} data-testid="pattern-apply-btn">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
            Apply Pattern Change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
